import { CRUDLoader } from "@/components/page/crudloader"
import { useDatabaseData, usePage } from "@/components/page/hooks"
import { agents } from "@/lib/agents/agent"
import { supabase } from "@/lib/data"
import { useForceUpdate } from "@/lib/utils"
import { useEffect, useRef, useState } from "react"
import { ulid } from "ulid"
import { ChatPanel } from "./ChatPanel"
import { ContactList } from "./ContactList"
import { Contact, Message, TAXONOMIES, Taxonomy } from "./types"

type Event = Table<"events">
type HistoryItem = Table<"history">
interface HistoryMessage {
  text: string
  type: "input_text" | "output_text"
}


export function EventsComponent() {

  const { team } = usePage()

  const { data, loading } = useDatabaseData("events", { filter: (supa) => supa.eq("team", team.id), })
  const update = useForceUpdate()
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null)

  const state = useRef({
    initialized: false,
    contacts: [] as Contact[],
    messages: [] as Message[],
  })

  if (!state.current.initialized && data && data.length > 0) {
    // console.log("Adding data")

    for (const event of data) {
      newEvent(event)
    }
    state.current.initialized = true
  }

  async function newEvent(event: Event) {

    const payload: HITLPayload = event.payload as any
    const s = state.current
    const { contacts } = s

    let contact: Contact = contacts.find(c => c.id === event.id)
    const now = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    let isNewContact = false

    if (!contact) {
      isNewContact = true

      const agent = await agents.loadAgent(event.agent, event.team)

      const causes: string[] = Array.isArray(payload.causes) ? payload.causes : []
      const normalize = (s: string) => s.replace(/\s+/g, "").toLowerCase()
      const taxonomy: Taxonomy[] = TAXONOMIES.filter(t => causes.some(c => normalize(c) === normalize(t.name ?? ""))).sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))

      contact = {
        id: event.id,
        name: payload.integration.name,
        type: taxonomy[0]?.emoji ?? "👋",
        unreadCount: 0,
        taxonomy,
        agent,
        team: event.team,
        integrationPayload: payload.integration
      }
      s.contacts = [...contacts, contact]
    }

    if (selectedContactId !== event.id) {
      contact.unreadCount++
    } else {
      contact.unreadCount = 0
    }
    contact.lastMessage = event.message
    contact.lastMessageTime = now

    update()

    if (isNewContact) {
      const { data: history, error } = await supabase.from("history").select("*").eq("uid", event.id).eq("type", "message").order("id", { ascending: true })
      // console.log(history)

      if (data) {
        const his = history as HistoryItem[]
        const newMessages: Message[] = []
        for (const h of his) {
          if (h.content == null || !Array.isArray(h.content)) continue
          const m: HistoryMessage = h.content[0] as any
          if (!m) continue
          if (m.type !== "input_text" && m.type !== "output_text") continue
          if (!m.text) continue
          const message: Message = {
            id: ulid(),
            contactId: event.id,
            content: m.text,
            direction: m.type === "input_text" ? "incoming" : "outgoing",
            status: "delivered",
            role: h.role == "user" ? "user" : h.role == "assistant" ? "assistant" : "human",
            timestamp: new Date(h.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          }
          newMessages.push(message)
        }
        const lastMessage = newMessages[newMessages.length - 1]
        if (lastMessage && lastMessage.content === event.message) s.messages.pop()
        s.messages = [...newMessages, ...s.messages]
      }
    } else {
      const message: Message = {
        id: ulid(),
        contactId: event.id,
        content: event.message,
        direction: "incoming",
        status: "delivered",
        role: "user",
        timestamp: now,
      }
      s.messages = [...s.messages, message]
    }

    update()

  }

  async function endEvent(event: Event) {

    const s = state.current
    const { contacts, messages } = s

    const idx = contacts.findIndex(c => c.id === event.id)

    if (idx > -1) {
      s.contacts.splice(idx, 1)
      s.messages = messages.filter(m => m.contactId !== event.id)
      update()
    }
  }

  useEffect(() => {
    // console.log("Realtime listening")
    const s = supabase.channel('realtime').on('postgres_changes', { event: '*', schema: 'public', table: "events" }, async payload => {
      console.log(payload.eventType, payload)
      if (payload.eventType == "INSERT") {
        await newEvent((payload.new || {}) as Event)
      } else if (payload.eventType == "DELETE") {
        await endEvent((payload.old || {}) as Event)
      } else if (payload.eventType == "UPDATE") {
        await newEvent((payload.new || {}) as Event)
      }
    }).subscribe()
    return () => { s.unsubscribe() }
  }, [])

  const { contacts, messages } = state.current

  const selectedContact = contacts.find(c => c.id === selectedContactId) ?? null
  const chatMessages = messages.filter(m => m.contactId === selectedContactId)

  const handleSelectContact = (id: string) => {
    setSelectedContactId(id)
    state.current.contacts = state.current.contacts.map(c => c.id === id ? { ...c, unreadCount: 0 } : c)
    update()
  }

  const handleSend = async (contactId: string, content: string, endChat: boolean) => {
    const now = new Date()
    const timeStr = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    const newMsg: Message = {
      id: `m${Date.now()}`,
      contactId,
      content,
      timestamp: timeStr,
      direction: "outgoing",
      status: "sent",
      role: "human",
    }
    const contact = state.current.contacts.find(c => c.id === contactId)
    if (contact) contact.unreadCount = 0
    state.current.messages = [...state.current.messages, newMsg]
    state.current.contacts = state.current.contacts.map(c => c.id === contactId ? { ...c, lastMessage: content, lastMessageTime: timeStr } : c)
    update()
    if (contact) {
      contact.unreadCount = 0
      await contact.agent.sendHumanHITLMessage(content, contact.id, contact.team, endChat, contact.integrationPayload)
    }
  }

  if (loading) return <CRUDLoader />

  return <div className="flex flex-col h-full overflow-hidden">
    {/* Top toolbar placeholder (1rem) */}
    <div className="h-10 shrink-0 bg-muted border-b border-border" />

    {/* Main chat interface */}
    <div className="flex flex-1 min-h-0">
      {/* Left: contact list (fixed width) */}
      <div className="w-80 shrink-0 flex flex-col min-h-0">
        <ContactList contacts={contacts} selectedId={selectedContactId} onSelect={handleSelectContact} />
      </div>

      {/* Right: chat panel */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        <ChatPanel contact={selectedContact} messages={chatMessages} onSend={handleSend} />
      </div>
    </div>
  </div>

}
